import React, { useState, useEffect } from 'react';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ShortcutCard, 
  ShortcutIconButton, 
  ShortcutLabel, 
  SuggestionsContainerShortcut, 
  CloseContainer,
  SuggestionItemContainer,
  ShortcutsContainer,
  LevelContainer,
  SuggestionsWrapper,
  CloseIcon,
  RoomChip,
  RoomsWrapper,
  ShortcutLabelInternal,
  primaryColor,
  ShortcutIconButton2,
  ShortcutLabelInternal2,
  ShortcutIconButton3,
  ShortcutLabelInternal3,
} from './FAQSearch.style';

import {
  centralLibraryTourData,
  musicLibraryTourData,
  hsslTourData,
  wbscTourData,
  lawLibraryTourData,
  mslTourData,
} from '../../config';

import { MdOutlineCalendarMonth, MdOutlineNearMe, MdPrint, MdEvent, MdWest } from 'react-icons/md';

const ShortcutPanel = ({ setActiveComponent, query, setQuery, showSuggestions, setShowInitialSuggestions,handleShortcutClick,handleInitialQuery,setInitialComponentClicked, onMapModeClick, mapLevel, onPrinterIssueClick, onStandaloneShortcutClick, locationPriority = 'Medicine + Science Library', routeLibraryKey = 'msl', serviceRouteKey = '', initialShortcut = null}) => {
    const [activeShortcut, setActiveShortcut] = useState(null);
    const [showShortcuts, setShowShortcuts]=useState(true);
    // Directory (Navigate) flow:
    // libraries -> msl | central | music | hssl | wbsc
    const [directoryView, setDirectoryView] = useState('libraries');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isCentralRoute = routeLibraryKey === 'clb';
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // When user picks a library in Navigate that belongs to a different kiosk route,
    // redirect there with ?shortcut=map&dir=<view> so the correct panel opens on arrival.
    const LIBRARY_NAVIGATE_ROUTES = {
      msl: '/msl_2_service',
      clb: '/clb_1_service',
      music: '/mus',
      law: '/law_1_service',
    };
    // Maps libraryKey → directoryView name
    const LIBRARY_DIR = { msl: 'msl', clb: 'central', music: 'music', law: 'law' };

    const switchToLibraryRoute = (targetLibraryKey) => {
      const targetRoute = LIBRARY_NAVIGATE_ROUTES[targetLibraryKey];
      if (targetRoute && `/${serviceRouteKey}` !== targetRoute) {
        const dir = LIBRARY_DIR[targetLibraryKey] || 'libraries';
        navigate(`${targetRoute}?shortcut=map&dir=${dir}`);
        return true;
      }
      return false;
    };

    // Auto-open Navigate on arrival from a cross-library redirect (?shortcut=map).
    // Also restores the target directory view from ?dir= so the user skips the library list.
    useEffect(() => {
      if (initialShortcut === 'map') {
        const dir = searchParams.get('dir') || 'libraries';
        setActiveShortcut('map');
        setShowInitialSuggestions(false);
        setShowShortcuts(false);
        setDirectoryView(dir);
        if (dir === 'msl') {
          handleShortcutClick("Where is ");
          setActiveComponent('wayfinding');
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialShortcut]);
    
  // Library service keys for component click tracking
  const LIBRARY_TRACK_KEYS = {
    central: 'clb_1_service',
    msl:     'msl_2_service',
    law:     'law_1_service',
    music:   'music',
    hssl:    'hssl',
    wbsc:    'wbsc',
  };

  const openTour = (url, roomName, libraryKey) => {
    const trackKey = libraryKey || LIBRARY_TRACK_KEYS[directoryView] || serviceRouteKey || routeLibraryKey;
    const componentClicked = `maps_${trackKey}`;
    const label = roomName ? `360° Tour: ${roomName}` : `360° Tour`;
    // Fire a standalone session record so the click is always captured
    onStandaloneShortcutClick(componentClicked, label);
    const newWindow = window.open(
      url,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    if (!newWindow) {
      alert('Please allow pop-ups to open the 360 tour, or use this link: ' + url);
    }
  };

  const isWebRoute = serviceRouteKey === 'web' || !serviceRouteKey;
  const withLibrary = (text) => isWebRoute ? text : `${text} in ${locationPriority}`;

  const renderLibraryDirectory = (data, libraryKey) => (
    <SuggestionsWrapper>
      {data.map((section) => (
        <LevelContainer key={section.lvl}>
          <ShortcutLabelInternal variant="body2">{section.lvl}</ShortcutLabelInternal>
          <RoomsWrapper>
            {section.rooms.map((room) => (
              <RoomChip
                key={room.name}
                label={room.name}
                variant="outlined"
                onClick={() => openTour(room.url, room.name, libraryKey)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </RoomsWrapper>
        </LevelContainer>
      ))}
    </SuggestionsWrapper>
  );

  const handleBorrowQuestionClick = (question, actionLabel) => {
    const actionComponent = `Book ${actionLabel}`;

    setQuery(question);
    setShowShortcuts(true);
    setActiveShortcut(null);
    setInitialComponentClicked(actionComponent);
    handleShortcutClick(question);
    handleInitialQuery(question, actionComponent);
  };

  const handleShortcutSelection = (shortcut) => {
    setActiveShortcut(shortcut);
    setShowInitialSuggestions(false);
    if (shortcut==='map'){
        setDirectoryView('libraries');
        setShowShortcuts(false);
    }
    else if (shortcut==='howToBorrow'){
      setShowShortcuts(false);
    }
    else if (shortcut==='book'){
      handleShortcutClick("How to Book a Space");
      handleInitialQuery("How to Book a Space", 'room/space booking');
      setInitialComponentClicked('room/space booking');
    }
    else if (shortcut==='printer'){
      setShowShortcuts(false);
    }
    else if (shortcut==='events'){
      const eventQuery = withLibrary("what events are happening currently and in the next one month");
      handleShortcutClick(eventQuery);
      handleInitialQuery(eventQuery, 'events');
      setInitialComponentClicked('events');
    }
    else if (shortcut === 'nusit'){
      if (onStandaloneShortcutClick) {
        onStandaloneShortcutClick('go_itcare', 'Go ITCARE');
      }
      const imgUrl = '/CL-to-ITCARE-map.jpg';
      const newWindow = window.open('', '_blank', 'width=980,height=760,scrollbars=yes,resizable=yes');
      if (!newWindow) {
        alert('Please allow pop-ups, then open: ' + imgUrl);
        return;
      }
      newWindow.document.write(`
        <html>
          <head><title>Go NUS IT Care</title></head>
          <body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;">
            <img src="${imgUrl}" alt="CL to IT Care Map" style="max-width:100%;max-height:100%;object-fit:contain;" />
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handlePrinterQuestionClick = (question, query) => {
    const queryWithLibrary = withLibrary(query);
    setQuery(queryWithLibrary);
    setShowShortcuts(true);
    setActiveShortcut(null);
    handleShortcutClick(queryWithLibrary);
    handleInitialQuery(queryWithLibrary, 'printer');
    setInitialComponentClicked('printer');
  };

  return (
    <ShortcutsContainer>
        {showShortcuts && (
          <ShortcutsContainer>
            {!isMobile && (
            <ShortcutCard onClick={() => handleShortcutSelection('map')}>
              <ShortcutIconButton>
                <MdOutlineNearMe />
              </ShortcutIconButton>
                <ShortcutLabel variant="caption">Navigate</ShortcutLabel>
            </ShortcutCard>
            )}

            <ShortcutCard onClick={() => handleShortcutSelection('book')}>
              <ShortcutIconButton>
                <MdOutlineCalendarMonth />
              </ShortcutIconButton>
              <ShortcutLabel variant="caption">Book a Space</ShortcutLabel>
            </ShortcutCard>

            <ShortcutCard onClick={() => handleShortcutSelection('howToBorrow')}>
              <ShortcutIconButton>
                <MenuBookIcon />
              </ShortcutIconButton>
              <ShortcutLabel variant="caption">Borrow Books</ShortcutLabel>
            </ShortcutCard>

            {!isWebRoute && (
              <ShortcutCard onClick={() => handleShortcutSelection('printer')}>
                <ShortcutIconButton>
                  <MdPrint />
                </ShortcutIconButton>
                <ShortcutLabel variant="caption">Find a Printer</ShortcutLabel>
              </ShortcutCard>
            )}

            <ShortcutCard onClick={() => handleShortcutSelection('events')}>
              <ShortcutIconButton>
                <MdEvent />
              </ShortcutIconButton>
              <ShortcutLabel variant="caption">Find Events</ShortcutLabel>
            </ShortcutCard>

            {isCentralRoute && !isWebRoute && (
              <ShortcutCard onClick={() => handleShortcutSelection('nusit')}>
                <ShortcutIconButton>
                  <MdOutlineNearMe />
                </ShortcutIconButton>
                <ShortcutLabel variant="caption">Visit ITCARE</ShortcutLabel>
              </ShortcutCard>
            )}
          </ShortcutsContainer>
        )}

        {activeShortcut === 'howToBorrow' && !showShortcuts && (
          <SuggestionsContainerShortcut>
            <CloseContainer>
              <SuggestionItemContainer>
                <ShortcutIconButton>
                  <MenuBookIcon style={{ fontSize: 12 }} />
                </ShortcutIconButton>
                <ShortcutLabelInternal variant="body2">
                  Borrow / Collect / Return
                </ShortcutLabelInternal>
              </SuggestionItemContainer>

              <CloseIcon
                onClick={() => {
                  setShowShortcuts(true);
                  setQuery('');
                  setActiveShortcut(null);
                }}
              />
            </CloseContainer>

            <SuggestionsWrapper>
              <RoomChip
                label="How to Borrow a Book from NUSearch+"
                onClick={() => handleBorrowQuestionClick(
                  "How to Borrow a Book from NUSearch+",
                  "Borrow"
                )}
                variant="outlined"
                style={{ margin: '0.5rem', cursor: 'pointer' }}
              />
              <RoomChip
                label={`Where to collect requested book from ${locationPriority}`}
                onClick={() => handleBorrowQuestionClick(
                  `Where to collect holdshelf book from ${locationPriority}`,
                  "Collect Request"
                )}
                variant="outlined"
                style={{ margin: '0.5rem', cursor: 'pointer' }}
              />
              <RoomChip
                label={`Where to return library book at ${locationPriority}`}
                onClick={() => handleBorrowQuestionClick(
                  `Where is the bookdrop at ${locationPriority}`,
                  "Return"
                )}
                variant="outlined"
                style={{ margin: '0.5rem', cursor: 'pointer' }}
              />
            </SuggestionsWrapper>
          </SuggestionsContainerShortcut>
        )}

        {activeShortcut === 'printer' && !showShortcuts && (
          <SuggestionsContainerShortcut>
            <CloseContainer>
              <SuggestionItemContainer>
                <ShortcutIconButton>
                  <MdPrint size={12} />
                </ShortcutIconButton>
                <ShortcutLabelInternal variant="body2">
                  Printer Help
                </ShortcutLabelInternal>
              </SuggestionItemContainer>

              <CloseIcon
                onClick={() => {
                  setShowShortcuts(true);
                  setQuery('');
                  setActiveShortcut(null);
                }}
              />
            </CloseContainer>

            <SuggestionsWrapper>
              <RoomChip
                label={`Where is the printer in ${locationPriority}?`}
                onClick={() => handlePrinterQuestionClick(
                  `Where is the printer in ${locationPriority}?`,
                  "where is the printer"
                )}
                variant="outlined"
                style={{ margin: '0.5rem', cursor: 'pointer' }}
              />
              <RoomChip
                label="Printer issues (Ink/Paper/Connection)?"
                onClick={() => {
                  setShowShortcuts(true);
                  setActiveShortcut(null);
                  if (onPrinterIssueClick) {
                    onPrinterIssueClick();
                    setInitialComponentClicked('printer');
                  }
                }}
                variant="outlined"
                style={{ margin: '0.5rem', cursor: 'pointer' }}
              />
            </SuggestionsWrapper>
          </SuggestionsContainerShortcut>
        )}

        {activeShortcut === 'map' && !showShortcuts && (
          <SuggestionsContainerShortcut>
            <CloseContainer>
              <SuggestionItemContainer>
                {/* Back button (only on sub-views) */}
                {directoryView !== 'libraries' && (
                  <ShortcutIconButton3
                    onClick={() => {
                      setDirectoryView('libraries');
                    }}
                    style={{ cursor: 'pointer' }}
                    aria-label="Back"
                  >
                    <MdWest size={16} />
                      <ShortcutLabelInternal3 variant="body2" style={{ paddingLeft: 0 }}>
                       {directoryView === 'central' 
                        ? 'Central Library:' 
                        : directoryView === 'msl' 
                          ? 'Medicine + Science Library' 
                           : directoryView === 'music'
                             ? 'Music Library'
                             : directoryView === 'hssl'
                               ? 'Hon Sui Sen Memorial Library'
                               : directoryView === 'wbsc'
                                 ? 'Wan Boo Sow Chinese Library'
                                 : directoryView === 'law'
                                   ? 'CJ Koh Law Library'
                                   : ''}
                      </ShortcutLabelInternal3>
                  </ShortcutIconButton3>
                )}
                { directoryView === 'libraries' &&  (
                <ShortcutIconButton2>
                  <MdOutlineNearMe color={primaryColor} size={16} />
                  <ShortcutLabelInternal2 variant="caption">
                    Select a Library
                  </ShortcutLabelInternal2>
                </ShortcutIconButton2>
                )}
               

                
              </SuggestionItemContainer>

              <CloseIcon
                onClick={() => {
                  setShowShortcuts(true);
                  setQuery('');
                  setActiveShortcut(null);
                  setDirectoryView('libraries');
                }}
              />
            </CloseContainer>

            {/* Step 1: pick library — sorted alphabetically */}
            {directoryView === 'libraries' && (() => {
              const libs = [
                { label: 'Central Library',              onClick: () => { onStandaloneShortcutClick('maps_clb_1_service', 'Navigate: Central Library');    if (switchToLibraryRoute('clb')) return; setDirectoryView('central'); } },
                { label: 'CJ Koh Law Library',           onClick: () => { onStandaloneShortcutClick('maps_law_1_service', 'Navigate: CJ Koh Law Library'); if (switchToLibraryRoute('law')) return; setDirectoryView('law'); } },
                { label: 'Hon Sui Sen Memorial Library', onClick: () => { onStandaloneShortcutClick('maps_hssl',           'Navigate: HSSL');                setDirectoryView('hssl'); } },
                { label: 'Medicine Science Library',     onClick: () => { onStandaloneShortcutClick('maps_msl_2_service',  'Navigate: MSL');                 if (switchToLibraryRoute('msl')) return; setDirectoryView('msl'); } },
                { label: 'Music Library',                onClick: () => { onStandaloneShortcutClick('maps_music',          'Navigate: Music Library');        if (switchToLibraryRoute('music')) return; setDirectoryView('music'); } },
                { label: 'Wan Boo Sow Chinese Library',  onClick: () => { onStandaloneShortcutClick('maps_wbsc',           'Navigate: WBSC');                setDirectoryView('wbsc'); } },
              ];
              return (
                <SuggestionsWrapper>
                  <RoomsWrapper>
                    {libs.map(({ label, onClick }) => (
                      <RoomChip key={label} label={label} variant="outlined" onClick={onClick} style={{ cursor: 'pointer' }} />
                    ))}
                  </RoomsWrapper>
                </SuggestionsWrapper>
              );
            })()}

            {directoryView === 'central' && renderLibraryDirectory(centralLibraryTourData, 'clb_1_service')}
            {directoryView === 'msl'     && renderLibraryDirectory(mslTourData,            'msl_2_service')}
            {directoryView === 'music'   && renderLibraryDirectory(musicLibraryTourData,   'music')}
            {directoryView === 'hssl'    && renderLibraryDirectory(hsslTourData,            'hssl')}
            {directoryView === 'wbsc'    && renderLibraryDirectory(wbscTourData,            'wbsc')}
            {directoryView === 'law'     && renderLibraryDirectory(lawLibraryTourData,      'law_1_service')}


          </SuggestionsContainerShortcut>
        )}
      </ShortcutsContainer>
  );
};

export default ShortcutPanel;
